import { Component, computed, input, signal } from '@angular/core';
import { endOfDay, isValid, parseISO, startOfDay } from 'date-fns';
import { FeatureFlags } from '../../../global/featureFlags/featureFlags';

export type MaintenanceBanner = {
  brands: string[];
  startDate?: string;
  endDate?: string;
  title: string;
  message: string;
  contactCentreMessage?: string;
  apologyMessage?: string;
};

@Component({
  selector: 'app-maintenance-banner',
  imports: [],
  templateUrl: './maintenance-banner.component.html',
  styleUrl: './maintenance-banner.component.scss',
  host: { '[class.hidden]': '!vm' },
})
export class MaintenanceBannerComponent {
  brand = input<string | undefined>();

  private readonly dismissed = signal(false);

  private readonly viewModel = computed(() => {
    if (this.dismissed()) return undefined;

    const brand = (this.brand() ?? '').trim().toLowerCase();
    if (!brand || !FeatureFlags.getFeatureFlag('maintenance-banner')) return undefined;

    const banners = FeatureFlags.getFeatureFlagPayload<MaintenanceBanner[]>('maintenance-banner');
    if (!Array.isArray(banners)) return undefined;

    const now = new Date();

    return banners.find((banner) => this.matchesBrand(banner, brand) && this.isActive(banner, now));
  });

  get vm() {
    return this.viewModel();
  }

  dismiss(): void {
    this.dismissed.set(true);
  }

  private matchesBrand(banner: MaintenanceBanner, brand: string): boolean {
    return (banner?.brands ?? []).some((configuredBrand) => {
      const prefix = (configuredBrand ?? '').trim().toLowerCase();
      return !!prefix && brand.startsWith(prefix);
    });
  }

  private isActive(banner: MaintenanceBanner, now: Date): boolean {
    const start = banner.startDate ? startOfDay(parseISO(banner.startDate)) : undefined;
    const end = banner.endDate ? endOfDay(parseISO(banner.endDate)) : undefined;
    if ((start && !isValid(start)) || (end && !isValid(end))) return false;

    return (!start || now >= start) && (!end || now <= end);
  }
}
